import { type } from 'arktype'

import { type Amount, amountSchema, amountToNumber, amountToString, gt, lt, newAmount } from '@orus.eu/amount'
import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { failure, isFailure, type Result, success } from '@orus.eu/result'

import {
  AbstractDimension,
  type DimensionMcpAdapter,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
  type DimensionOptions,
} from './abstract-dimension.ts'

export class AmountDimension<NAME extends string> extends AbstractDimension<NAME, Amount> {
  override validateData(value: LooselyTypedValue): Result<Amount, DimensionValidationProblem> {
    if (typeof value !== 'object' || value === null)
      return dimensionValidationFailure(`Field ${this.name} is not an Amount`)
    if (!('__encodedAmount' in value))
      return dimensionValidationFailure(
        `Field ${this.name} is not an Amount because property "__encodedAmount" is missing`,
      )
    const { __encodedAmount } = value
    if (typeof __encodedAmount !== 'number') {
      return dimensionValidationFailure(
        `Field ${this.name} is not an Amount because property "__encodedAmount" is not a number`,
      )
    }
    if (!Number.isSafeInteger(__encodedAmount)) {
      return dimensionValidationFailure(
        `Field ${this.name} is not an Amount because property "__encodedAmount" is not a safe integer`,
      )
    }
    return success({ __encodedAmount })
  }

  override formatToString(value: Amount): string {
    return amountToString(value, { displayDecimals: false, addCurrency: true })
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<number> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.number(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: Amount | null | undefined): number | null => {
      if (!stateValue) return null
      return amountToNumber(stateValue)
    },
  }

  override readonly mcpAdapter: DimensionMcpAdapter<Amount> = {
    parseValueFromMcp(mcpValueInput: string): Result<Amount, string> {
      const numberValue = parseFloat(mcpValueInput)
      if (isNaN(numberValue)) {
        return failure(
          `Invalid amount: Provide the number of euros, with a maximum of 2 decimal places. Must be compatible with parseFloat.`,
        )
      }
      return success(newAmount(numberValue))
    },
    formattingInstructions: `For this tool, you must specify the number of euros, with a maximum of 2 decimal places. Must be compatible with parseFloat.`,
  }
}

export function formatAmountDimensionValue(value: Amount): string {
  return amountToString(value, { displayDecimals: 'when-amount-has-cents', addCurrency: true })
}

export function formatRoundedAmountDimensionValue(value: Amount): string {
  return amountToString(value, { displayDecimals: false, addCurrency: true })
}

export const amountLimitsType = type({
  min: amountSchema.optional(),
  max: amountSchema.optional(),
  step: amountSchema.optional(),
})

export function areAmountLimitsEqual(a: AmountLimitsConfiguration, b: AmountLimitsConfiguration): boolean {
  return a.min === b.min && a.max === b.max && a.step === b.step
}

export type ContradictoryAmountLimitsProblem = { type: 'contradictory_amount_limits'; min: Amount; max: Amount }

export function getContradictoryAmountLimits(
  limitConfiguration: AmountLimitsConfiguration,
): { min: Amount; max: Amount } | undefined {
  const { min, max } = limitConfiguration
  if (!min || !max || !gt(min, max)) return undefined
  return { min, max }
}

export function getValidAmountForLimitConfiguration(
  limitConfiguration: AmountLimitsConfiguration,
  preferredAmount: Amount,
): Result<Amount, ContradictoryAmountLimitsProblem> {
  const contradictoryLimits = getContradictoryAmountLimits(limitConfiguration)
  if (contradictoryLimits) {
    return failure({ type: 'contradictory_amount_limits', ...contradictoryLimits })
  }
  if (limitConfiguration.min && gt(limitConfiguration.min, preferredAmount)) {
    return success(limitConfiguration.min)
  }
  if (limitConfiguration.max && lt(limitConfiguration.max, preferredAmount)) {
    return success(limitConfiguration.max)
  }
  return success(preferredAmount)
}

/**
 * Returns undefined for an inverted range, which contains no amount to clamp into. Callers must decide what to use
 * instead: returning either bound would make the default allocation trigger alternate between them rather than reach
 * a fixpoint, and exclusionFrPrelKanonikContradictoryAmountLimits blocks the quote downstream anyway.
 */
export function clampAmountToLimits(
  amount: Amount,
  limits: AmountLimitsConfiguration | null | undefined,
): Amount | undefined {
  if (!limits) return amount
  const validAmount = getValidAmountForLimitConfiguration(limits, amount)
  return isFailure(validAmount) ? undefined : validAmount.output
}

export type AmountLimitsConfiguration = typeof amountLimitsType.infer

export class AmountLimitsDimension<NAME extends string> extends AbstractDimension<NAME, AmountLimitsConfiguration> {
  constructor(options: DimensionOptions<NAME, AmountLimitsConfiguration>) {
    super(options)
  }

  override validateData(value: LooselyTypedValue): Result<AmountLimitsConfiguration, DimensionValidationProblem> {
    const result = amountLimitsType(value)
    if (result instanceof type.errors) {
      return dimensionValidationFailure(`Field ${this.name} is not an AmountLimitsConfiguration`)
    }
    return success(result)
  }

  formatToString(value: AmountLimitsConfiguration): string {
    const parts: string[] = []
    if (value.min) {
      parts.push(`min: ${amountToString(value.min, { displayDecimals: false, addCurrency: true })}`)
    }
    if (value.max) {
      parts.push(`max: ${amountToString(value.max, { displayDecimals: false, addCurrency: true })}`)
    }
    if (value.step) {
      parts.push(`step: ${amountToString(value.step, { displayDecimals: false, addCurrency: true })}`)
    }
    return parts.join(', ')
  }
}
